import $ from "dabbyjs/src/core/dabby/dabby.js";
import "dabbyjs/src/core/each/each.js";
import "dabbyjs/src/attributes/data/data.js";
import "dabbyjs/src/events/on/on.js";
import "dabbyjs/src/traversal/find/find.js";

// colour scale: light tint (#dbe8ff) -> brand blue (#1F3D7C)
const toColour = (n, max) => {
	if (!n || !max) {
		return "#e8e8e8";
	}
	const t = Math.pow(n / max, 0.5),
		r = Math.round(219 + (31  - 219) * t),
		g = Math.round(232 + (61  - 232) * t),
		b = Math.round(255 + (124 - 255) * t);
	return "rgb(" + r + "," + g + "," + b + ")";
};

// LA coverage choropleth: pan/zoom + hover tooltips, driven by data-map on the wrapper.
export default obj => {
	if (!obj.length) {
		return;
	}

	obj.each(function () {
		const $wrap = $(this),
			cfg   = $wrap.data("map") || {},
			data  = cfg.data || {},
			max   = cfg.max  || 0,
			click = cfg.click || null,
			svg   = this.querySelector(".report__chart-map-svg");

		if (!svg) {
			return;
		}

		const tooltip      = this.querySelector(".report__chart-map-tooltip"),
			tooltipName  = tooltip.querySelector(".report__chart-map-tooltip-name"),
			tooltipCount = tooltip.querySelector(".report__chart-map-tooltip-count");

		// --- zoom / pan state ---
		// viewBox dimensions come from the chart config (cfg.vb = [w, h]) so the same map engine
		// drives any SVG (UK LA map, world map, …); falls back to the UK map's dimensions.
		const VB_W = (cfg.vb && cfg.vb[0]) || 1626.9587,
			VB_H = (cfg.vb && cfg.vb[1]) || 3039.864,
			ZOOM_STEP = 0.5,
			MIN_ZOOM = 1,
			MAX_ZOOM = 8;
		let vb = { x: 0, y: 0, w: VB_W, h: VB_H };

		const applyViewBox = () => {
			svg.setAttribute("viewBox", vb.x + " " + vb.y + " " + vb.w + " " + vb.h);
		};

		const clamp = () => {
			if (vb.x < 0) { vb.x = 0; }
			if (vb.y < 0) { vb.y = 0; }
			if (vb.x + vb.w > VB_W) { vb.x = VB_W - vb.w; }
			if (vb.y + vb.h > VB_H) { vb.y = VB_H - vb.h; }
		};

		const zoom = (factor, cx, cy) => {
			cx = cx !== undefined ? cx : vb.x + vb.w / 2;
			cy = cy !== undefined ? cy : vb.y + vb.h / 2;
			const newW  = Math.min(Math.max(VB_W / MAX_ZOOM, vb.w / factor), VB_W / MIN_ZOOM),
				scale = newW / vb.w;
			vb.x = cx - (cx - vb.x) * scale;
			vb.y = cy - (cy - vb.y) * scale;
			vb.w = newW;
			vb.h = VB_H * (newW / VB_W);
			clamp();
			applyViewBox();
		};

		const clientToVb = (cx, cy) => {
			const rect = svg.getBoundingClientRect();
			return {
				x: vb.x + (cx - rect.left) / rect.width  * vb.w,
				y: vb.y + (cy - rect.top)  / rect.height * vb.h
			};
		};

		// button controls
		$wrap.find("[data-zoom='in']").on("click", () => zoom(1 + ZOOM_STEP));
		$wrap.find("[data-zoom='out']").on("click", () => zoom(1 / (1 + ZOOM_STEP)));
		$wrap.find("[data-zoom='reset']").on("click", () => {
			vb = { x: 0, y: 0, w: VB_W, h: VB_H };
			applyViewBox();
		});

		// mouse wheel zoom (focal point = cursor). Bound natively with passive:false because
		// dabby registers wheel listeners as passive, which would no-op preventDefault and let
		// the page scroll behind the map.
		svg.addEventListener("wheel", e => {
			e.preventDefault();
			const pt = clientToVb(e.clientX, e.clientY);
			zoom(e.deltaY < 0 ? 1 + ZOOM_STEP : 1 / (1 + ZOOM_STEP), pt.x, pt.y);
		}, { passive: false });

		// drag to pan. `dragged` tracks whether the pointer actually moved, so a click handler
		// can tell a genuine click from the mouseup at the end of a pan.
		let drag = null,
			dragged = false;
		$(svg).on("mousedown", e => {
			if (e.button !== 0) {
				return;
			}
			drag = { sx: e.clientX, sy: e.clientY, vx: vb.x, vy: vb.y };
			dragged = false;
			svg.style.cursor = "grabbing";
		});
		$(window).on("mousemove", e => {
			if (!drag) {
				return;
			}
			if (Math.abs(e.clientX - drag.sx) > 3 || Math.abs(e.clientY - drag.sy) > 3) {
				dragged = true;
			}
			const rect = svg.getBoundingClientRect(),
				dx = (e.clientX - drag.sx) / rect.width  * vb.w,
				dy = (e.clientY - drag.sy) / rect.height * vb.h;
			vb.x = drag.vx - dx;
			vb.y = drag.vy - dy;
			clamp();
			applyViewBox();
		});
		$(window).on("mouseup", () => {
			if (!drag) {
				return;
			}
			drag = null;
			svg.style.cursor = "";
		});

		const positionTooltip = e => {
			const rect = this.getBoundingClientRect(),
				tw = tooltip.offsetWidth,
				th = tooltip.offsetHeight;
			let x = e.clientX - rect.left + 12,
				y = e.clientY - rect.top  - 10;
			if (x + tw > rect.width)  { x = e.clientX - rect.left - tw - 12; }
			if (y + th > rect.height) { y = e.clientY - rect.top  - th - 10; }
			tooltip.style.left = x + "px";
			tooltip.style.top  = y + "px";
		};

		// Perform the configured click action for an area. Abstracted so the behaviour is
		// driven by the chart config (cfg.click), not hardcoded here:
		//   { type:'filter', param:<url var>, value:<field on the area entry> }
		//     → set ?<param>=<entry[value]> on the current URL and navigate (applies the filter).
		// Unknown/absent action types are a no-op, so adding new behaviours later is config + a
		// new branch here, without touching the per-area wiring.
		const performClick = (entry, name) => {
			if (!click || !click.type) {
				return;
			}
			if (click.type === "filter") {
				const value = entry && click.value && entry[click.value] !== undefined ? entry[click.value] : name,
					url   = new URL(window.location.href);
				url.searchParams.set(click.param, value);
				url.searchParams.delete("page"); // reset paging when the filter changes
				window.location.href = url.toString();
			}
		};

		const names = cfg.names || {};

		// Colour AND make interactive every path fragment, keyed by data-la (the area/country code).
		// A country can be many fragments (mainland + islands); attaching events to all of them means
		// the whole country is hoverable/clickable, not just the one fragment that carries the <title>.
		// The display name comes from the data entry's label, else the names map, else the raw code.
		$(svg).find("path[data-la]").each(function () {
			const key   = this.getAttribute("data-la"),
				entry = data[key] || null,
				count = entry ? entry.count : 0,
				label = (entry && entry.label) || names[key] || key;
			this.style.fill = toColour(count, max);
			this.style.cursor = "pointer";

			$(this).on("mouseenter", e => {
				tooltipName.textContent  = label;
				tooltipCount.textContent = count ? count + " subscriber" + (count !== 1 ? "s" : "") : "No subscribers";
				tooltip.setAttribute("aria-hidden", "false");
				tooltip.classList.add("report__chart-map-tooltip--visible");
				positionTooltip(e);
			});
			$(this).on("mousemove", positionTooltip);
			$(this).on("mouseleave", () => {
				tooltip.classList.remove("report__chart-map-tooltip--visible");
				tooltip.setAttribute("aria-hidden", "true");
			});

			// click to act (e.g. filter). Skipped after a pan so dragging the map never fires it.
			if (click && click.type) {
				$(this).on("click", () => {
					if (dragged) {
						return;
					}
					performClick(entry, key);
				});
			}
		});
	});
};
